import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { sdk } from '@/services/sdk';
import { Input } from '@/components/ui/input';
import { Field } from '@/components/kit';
import { FormDialog, SwitchRow } from '@/components/inputs';
import { committed } from '@/shared/data';
import { formatIn } from '@/shared/currency';

/** "CA$38.64" (or "¥5,980") for a USD amount in a currency record. */
export function convert(usd, currency) {
  return formatIn(usd, currency);
}

function fromCurrency(c) {
  return {
    Name: c?.Name ?? '',
    symbol_c: c?.symbol_c ?? '',
    rate_c: c?.rate_c != null ? String(c.rate_c) : '',
    active_c: c ? !!c.active_c : true,
  };
}

/** Add or edit a display currency. Prices stay in USD; the rate converts them for show. */
export default function CurrencyDialog({ open, onOpenChange, currency, onSaved }) {
  const editing = !!currency;
  const [form, setForm] = useState(fromCurrency(currency));
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) setForm(fromCurrency(currency));
  }, [open]);

  const set = (key) => (value) => setForm((f) => ({ ...f, [key]: value }));
  const text = (key) => (e) => set(key)(e.target.value);

  async function submit() {
    const code = form.Name.trim().toUpperCase();
    if (!/^[A-Z]{3}$/.test(code)) {
      toast.error('Use the three-letter code, like CAD or EUR');
      return;
    }
    if (!form.symbol_c.trim()) {
      toast.error('Add the symbol');
      return;
    }
    const rate = Number(form.rate_c);
    if (!(rate > 0)) {
      toast.error('Add how many units one US dollar buys');
      return;
    }
    const fields = {
      Name: code,
      symbol_c: form.symbol_c.trim(),
      rate_c: Math.round(rate * 10000) / 10000,
      active_c: !!form.active_c,
    };
    setSaving(true);
    try {
      const res = editing
        ? await sdk.table('currencies_c').update({ Id: currency.Id, ...fields })
        : await sdk.table('currencies_c').create(fields);
      const saved = committed(res, editing ? 'Currency updated' : 'Currency added');
      if (!saved) return;
      onOpenChange(false);
      setForm(fromCurrency(null));
      onSaved?.(saved);
    } finally {
      setSaving(false);
    }
  }

  return (
    <FormDialog
      open={open}
      onOpenChange={onOpenChange}
      title={editing ? `Edit ${currency.Name}` : 'Add a currency'}
      description="Checkout charges in USD. Other currencies show an estimate next to the price."
      onSubmit={submit}
      saving={saving}
      submitLabel={editing ? 'Save changes' : 'Add currency'}
      busyLabel={editing ? 'Saving…' : 'Adding…'}
    >
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Code" htmlFor="cu-code">
          <Input id="cu-code" value={form.Name} onChange={text('Name')} placeholder="CAD" className="font-mono uppercase" maxLength={3} />
        </Field>
        <Field label="Symbol" htmlFor="cu-symbol">
          <Input id="cu-symbol" value={form.symbol_c} onChange={text('symbol_c')} placeholder="CA$" />
        </Field>
      </div>
      <Field label="Rate per US dollar" htmlFor="cu-rate" hint="1 USD buys this many. Keep USD itself at 1.">
        <Input id="cu-rate" type="number" min="0" step="0.0001" value={form.rate_c} onChange={text('rate_c')} placeholder="1.3800" />
      </Field>
      <SwitchRow id="cu-active" label="Shown at checkout" checked={form.active_c} onChange={set('active_c')} />
    </FormDialog>
  );
}
